import { useEffect, useState } from "react";
import api from "../services/api";
import useAuthStore from "../store/authStore";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Loader2, Crown, Building2, MapPin, Users, IndianRupee, TrendingUp, ShieldCheck, Rocket, ArrowRight,
} from "lucide-react";

const roleLabels = {
  founder: "Founder",
  ceo: "CEO",
  admin: "Admin",
  main_hub_manager: "Main Hub Manager",
  micro_hub_lead: "Micro Hub Lead",
  agent: "Gig Worker",
  ops_staff: "Ops Staff",
  user: "Customer",
};

export default function FounderDashboard() {
  const { user } = useAuthStore();
  const isFounder = user?.role === "founder";
  const [overview, setOverview] = useState(null);
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteForm, setPromoteForm] = useState({ user_id: "", role: "ceo", hub_id: "" });
  const [saving, setSaving] = useState(false);
  // Sprint 71 — Sample Order Generator state
  const [samples, setSamples] = useState([]);
  const [sampling, setSampling] = useState(false);

  const load = async () => {
    try {
      const [{ data: ov }, { data: hb }] = await Promise.all([
        api.get("/hierarchy/exec/overview"),
        api.get("/hierarchy/hubs"),
      ]);
      setOverview(ov);
      setHubs(hb.hubs || []);
    } catch (e) {
      toast.error("Failed to load executive overview");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); loadSamples(); }, []);

  const loadSamples = async () => {
    try {
      const { data } = await api.get("/sample-order/list", { params: { limit: 8 } });
      setSamples(data.orders || []);
    } catch { /* */ }
  };

  const generateSample = async () => {
    setSampling(true);
    try {
      const { data } = await api.post("/sample-order", {});
      toast.success(`Sample order ${data.tracking_id} created · ₹${data.price}`);
      loadSamples();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create sample order");
    } finally {
      setSampling(false);
    }
  };

  const advanceSample = async (tid) => {
    try {
      const { data } = await api.post(`/sample-order/${tid}/advance`);
      toast.success(`${tid} → ${data.status}`);
      loadSamples();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Advance failed");
    }
  };

  const submitPromote = async () => {
    if (!promoteForm.user_id || !promoteForm.role) {
      toast.error("User ID and role are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/hierarchy/exec/promote", promoteForm);
      toast.success("User promoted");
      setPromoteOpen(false);
      setPromoteForm({ user_id: "", role: "ceo", hub_id: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 flex items-center gap-3"><Loader2 className="animate-spin h-6 w-6 text-orange-500" /> Loading executive overview...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="founder-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Crown className="h-7 w-7 text-amber-500" />
            {isFounder ? "Founder Overview" : "CEO / Central Operations"}
          </h1>
          <p className="text-slate-500 mt-1">Read-only board view across all hubs, GMV, expansion pipeline.</p>
        </div>
        {isFounder && (
          <Button onClick={() => setPromoteOpen(true)} className="bg-amber-500 hover:bg-amber-600" data-testid="founder-promote-btn">
            <ShieldCheck className="h-4 w-4 mr-2" /> Promote User
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Kpi icon={<Building2 className="h-5 w-5" />} label="Total Hubs" value={overview?.hubs?.total ?? 0} sub={`${overview?.hubs?.main ?? 0} main · ${overview?.hubs?.micro ?? 0} micro · ${overview?.hubs?.campus ?? 0} campus`} />
        <Kpi icon={<IndianRupee className="h-5 w-5" />} label="GMV (Delivered)" value={`₹${Math.round(overview?.gmv_inr || 0).toLocaleString("en-IN")}`} />
        <Kpi icon={<TrendingUp className="h-5 w-5" />} label="Active Orders" value={overview?.orders?.in_progress ?? 0} sub={`${overview?.orders?.delivered ?? 0} delivered`} />
        <Kpi icon={<Users className="h-5 w-5" />} label="Team" value={Object.values(overview?.role_counts || {}).reduce((a, b) => a + b, 0)} sub="all roles" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Team composition">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Count</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(overview?.role_counts || {}).sort((a,b) => b[1]-a[1]).map(([r, c]) => (
                <TableRow key={r}>
                  <TableCell>{roleLabels[r] || r}</TableCell>
                  <TableCell className="text-right font-semibold">{c}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Card title="Top Main Hubs · last 30 days">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Hub</TableHead>
                <TableHead>City</TableHead>
                <TableHead className="text-right">Orders 30d</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(overview?.top_main_hubs_30d || []).map((h) => (
                <TableRow key={h.hub_id}>
                  <TableCell className="font-medium">{h.name}</TableCell>
                  <TableCell className="text-slate-500">{h.city}</TableCell>
                  <TableCell className="text-right font-semibold text-orange-600">{h.orders_30d}</TableCell>
                </TableRow>
              ))}
              {(overview?.top_main_hubs_30d || []).length === 0 && (
                <TableRow><TableCell colSpan={3} className="text-center text-slate-400 py-4">No hubs yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Card title={
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2"><Rocket className="h-5 w-5 text-purple-500" /> Demo / QA · Sample Order Flow</span>
          <Button onClick={generateSample} disabled={sampling} size="sm" className="bg-purple-500 hover:bg-purple-600" data-testid="generate-sample-order-btn">
            {sampling ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Rocket className="h-3.5 w-3.5 mr-1.5" /> Generate sample order</>}
          </Button>
        </div>
      } className="mt-6">
        <p className="text-xs text-slate-500 mb-3">
          Fires a fake order through your real routing engine. Use the &quot;Advance&quot; button to push it through every stage (Micro → Main → Linehaul → Main → Micro → Delivered) and watch the Micro Hub Lead and Ops Staff dashboards light up in real time.
        </p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tracking</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Origin Hub</TableHead>
              <TableHead>Destination Hub</TableHead>
              <TableHead>Current</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {samples.map((o) => (
              <TableRow key={o.tracking_id}>
                <TableCell className="font-mono text-xs">{o.tracking_id}</TableCell>
                <TableCell><span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs">{o.status}</span></TableCell>
                <TableCell className="text-xs font-mono">{o.origin_hub_id}</TableCell>
                <TableCell className="text-xs font-mono">{o.destination_hub_id}</TableCell>
                <TableCell className="text-xs font-mono">{o.current_hub_id || "—"}</TableCell>
                <TableCell className="text-right">
                  {o.status !== "delivered" && (
                    <Button onClick={() => advanceSample(o.tracking_id)} size="sm" variant="outline" data-testid={`advance-sample-${o.tracking_id}`}>
                      Advance <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {samples.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-4">No sample orders yet. Click &quot;Generate sample order&quot; above.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Card title="All Hubs" className="mt-6">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Hub</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>City</TableHead>
              <TableHead>Manager</TableHead>
              <TableHead>Parent</TableHead>
              <TableHead className="text-right">Children</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {hubs.map((h) => (
              <TableRow key={h.hub_id}>
                <TableCell className="font-medium">{h.name}</TableCell>
                <TableCell><HubBadge t={h.hub_type} /></TableCell>
                <TableCell>{h.city}</TableCell>
                <TableCell>{h.manager?.name || <span className="text-slate-400 italic">Unassigned</span>}</TableCell>
                <TableCell className="text-xs text-slate-500">{h.parent_hub_id || "—"}</TableCell>
                <TableCell className="text-right">{h.child_count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={promoteOpen} onOpenChange={setPromoteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Promote User</DialogTitle>
            <DialogDescription>Founder action: change a user's role. Use carefully.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>User ID (Mongo _id)</Label>
              <Input value={promoteForm.user_id} onChange={(e) => setPromoteForm({ ...promoteForm, user_id: e.target.value })} placeholder="e.g. 65abf..." data-testid="promote-user-id" />
            </div>
            <div>
              <Label>Role</Label>
              <select className="w-full border rounded-lg p-2" value={promoteForm.role} onChange={(e) => setPromoteForm({ ...promoteForm, role: e.target.value })} data-testid="promote-role">
                <option value="ceo">CEO</option>
                <option value="admin">Admin</option>
                <option value="main_hub_manager">Main Hub Manager</option>
                <option value="micro_hub_lead">Micro Hub Lead</option>
                <option value="agent">Gig Worker (agent)</option>
                <option value="ops_staff">Ops Staff</option>
                <option value="user">Customer (demote)</option>
              </select>
            </div>
            <div>
              <Label>Attach to Hub (optional)</Label>
              <Input value={promoteForm.hub_id} onChange={(e) => setPromoteForm({ ...promoteForm, hub_id: e.target.value })} placeholder="HUB-XXXXXX" />
            </div>
            <Button onClick={submitPromote} disabled={saving} className="w-full" data-testid="promote-submit">
              {saving ? <Loader2 className="animate-spin h-4 w-4" /> : "Promote"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
      <div className="flex items-center justify-between text-slate-500 text-xs uppercase tracking-wider">
        <span>{label}</span>{icon}
      </div>
      <div className="text-2xl font-bold mt-2">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

function Card({ title, children, className = "" }) {
  return (
    <div className={`bg-white rounded-2xl border border-slate-100 p-5 shadow-sm ${className}`}>
      <h3 className="font-semibold text-slate-800 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function HubBadge({ t }) {
  const map = {
    main: "bg-orange-100 text-orange-700",
    micro: "bg-blue-100 text-blue-700",
    campus: "bg-emerald-100 text-emerald-700",
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[t] || "bg-slate-100 text-slate-600"}`}>{t || "main"}</span>;
}
